import { apiConfig } from 'core-lib/config/config';
import { MethodData, MethodGroupData } from '../types/Model';
import { PaymentInstrument } from '../constants/PaymentInstrument';
import { getDefaultHeaders } from 'core-lib/utils/network';
import { postMethod } from 'core-lib/utils/apiV2';
import { checkEligibility } from './checkEligibility';
import fetchLastUsed from './fetchLastUsed';
import { firePaymentMethodListFetched } from '../utils/events';

type MethodListResultTypes = {
  response?: {
    allMethods: MethodGroupData[];
    autoPayMethods: MethodGroupData[];
  };
  error?: any;
};

export async function serializeMethod(pItem: any, amount: number): Promise<MethodData> {
  if (!pItem) return null;
  let eligibilityResponse = null;
  if (pItem?.payment_payload?.eligibility_url) {
    const { response } = await checkEligibility(pItem?.payment_payload?.eligibility_url, amount);
    if (response) eligibilityResponse = response;
  }
  return {
    eligibilityApiUrl: pItem?.payment_payload?.eligibility_url,
    paymentPartner: pItem.payment_partner_name?.toLowerCase(),
    paymentInstrument: pItem.payment_method,
    paymentApp: pItem.method_name,
    displayName: pItem.display_name,
    description: pItem.description,
    icon: pItem.image,
    id: pItem.display_name,
    paymentByNative: pItem?.payment_by_native ?? null,
    bankCode: pItem?.payment_payload?.issuer ?? null,
    cardInfo:
      pItem.payment_method === PaymentInstrument.Card
        ? {
            network: pItem.payment_payload?.network,
            token: pItem.payment_payload?.token_id,
            savedCardId: pItem.payment_payload?.saved_card_id,
            iin: pItem.payment_payload?.iin,
            type: pItem.payment_payload?.card_type,
            isCvvLess: pItem.payment_payload?.cvv_less_supported,
            number: pItem.description,
            owner: pItem.display_name,
            cvvTitle: pItem.payment_payload?.cvv_title,
            cp: pItem.payment_payload?.cp,
          }
        : null,
    rawData: pItem,
    availability: {
      status: pItem?.availability?.status ?? false,
      msg: pItem?.availability?.msg ?? 'Currently facing high failure rates',
    },
    eligibilityResponse: eligibilityResponse ?? null,
    uiType: pItem.ui_type,
  };
}
export async function serializeAllMethods(data: any, amount = 0): Promise<MethodGroupData[]> {
  const result: MethodGroupData[] = [];

  if (data) {
    for (const method of data) {
      const methodData = {
        logo: method?.display_logo ?? '',
        title: method.display_name,
        list: [],
      };
      if (method.options) {
        for (const option of method.options) {
          const item = await serializeMethod(option, amount);
          methodData.list.push(item);
        }
      }
      result.push(methodData);
    }
  }
  return result;
}

const fetchMethodList = async (body: any, amount: number): Promise<MethodListResultTypes> => {
  const endPoint = `${apiConfig('payment', 'latest')}/payments/payment-method-list`;

  const headers = getDefaultHeaders({
    deviceOs: true,
    deviceId: true,
    effectivePlatform: true,
    nativeAppVersion: false,
    packageName: true,
  });

  const { response, error }: any = await postMethod({
    endPoint,
    body,
    headers,
    enableErrorHandling: true,
  });

  if (response) {
    const formattedResponse: any = {};

    if (response?.data) {
      firePaymentMethodListFetched({ upiApps: body?.upi_apps, vendorId: body?.vendor_id });
      if (response.data.payment_methods?.length) {
        formattedResponse.allMethods = await serializeAllMethods(
          response.data.payment_methods,
          amount,
        );
      }
      if (response.data.autopay_payment_methods?.length) {
        formattedResponse.autoPayMethods = await serializeAllMethods(
          response.data.autopay_payment_methods,
          amount,
        );
      }

      return { response: formattedResponse };
    } else {
      return { error: {} };
    }
  }
  if (error) {
    return { error };
  }
};

export default fetchMethodList;
